/**
 * UTC Date & Time Formatting Utilities for Zeta Network Admin
 * Strict UTC compliance: Backend provides ISO 8601 strings in UTC (YYYY-MM-DDTHH:mm:ss.sssZ).
 */

/**
 * Format an ISO date string to: "20 Aug 2026, 05:30 PM (UTC)"
 * @param {string|Date} dateStr
 * @param {Object} [options]
 * @param {boolean} [options.showUtcBadge=true]
 * @returns {string}
 */
export const formatUtcDateTime = (dateStr, { showUtcBadge = true } = {}) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);

    const dateFormatted = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

    const timeFormatted = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    return `${dateFormatted}, ${timeFormatted}${showUtcBadge ? " (UTC)" : ""}`;
  } catch {
    return String(dateStr);
  }
};

/**
 * Format ISO string to UTC with seconds: "20 Aug 2026, 05:30:15 PM UTC"
 * @param {string|Date} [date]
 * @returns {string}
 */
export const formatUtcDateTimeWithSeconds = (date = new Date()) => {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "—";

    const dateFormatted = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

    const timeFormatted = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    return `${dateFormatted}, ${timeFormatted} UTC`;
  } catch {
    return "—";
  }
};

/**
 * Format date in user's local timezone with timezone name: "20 Aug 2026, 11:00 PM (Asia/Kolkata)"
 * @param {string|Date} [date]
 * @param {Object} [options]
 * @param {boolean} [options.showSeconds=false]
 * @returns {string}
 */
export const formatLocalDateTime = (date = new Date(), { showSeconds = false } = {}) => {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "—";

    const dateFormatted = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const timeFormatted = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: showSeconds ? "2-digit" : undefined,
      hour12: true,
    });

    let tz = "";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {}

    return `${dateFormatted}, ${timeFormatted}${tz ? ` (${tz})` : ""}`;
  } catch {
    return "—";
  }
};

/**
 * Format an ISO date string to: "20 Aug 2026 (UTC)" or "20 Aug 2026"
 * @param {string|Date} dateStr
 * @param {Object} [options]
 * @param {boolean} [options.showUtcBadge=true]
 * @returns {string}
 */
export const formatUtcDate = (dateStr, { showUtcBadge = true } = {}) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);

    const dateFormatted = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

    return `${dateFormatted}${showUtcBadge ? " (UTC)" : ""}`;
  } catch {
    return String(dateStr);
  }
};

/**
 * Format UTC time only: "05:30 PM (UTC)"
 * @param {string|Date} dateStr
 * @returns {string}
 */
export const formatUtcTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);

    const timeFormatted = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    return `${timeFormatted} (UTC)`;
  } catch {
    return String(dateStr);
  }
};

/**
 * Convert ISO string to datetime-local value in UTC coordinates (YYYY-MM-DDTHH:mm)
 * @param {string} isoString
 * @returns {string}
 */
export const isoToUtcInput = (isoString) => {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

/**
 * Convert datetime-local value (treated as UTC) to standard ISO string
 * @param {string} inputValue - format "YYYY-MM-DDTHH:mm"
 * @returns {string}
 */
export const utcInputToIso = (inputValue) => {
  if (!inputValue) return "";
  try {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(inputValue)) {
      return `${inputValue}:00.000Z`;
    }
    const d = new Date(inputValue);
    return isNaN(d.getTime()) ? "" : d.toISOString();
  } catch {
    return "";
  }
};

/**
 * Given a UTC datetime-local input string (YYYY-MM-DDTHH:mm), return the formatted UTC string
 * @param {string} inputValue
 * @returns {string}
 */
export const formatUtcInputDisplay = (inputValue) => {
  if (!inputValue) return "";
  try {
    const d = new Date(`${inputValue}:00.000Z`);
    if (isNaN(d.getTime())) return "";
    return formatUtcDateTime(d);
  } catch {
    return "";
  }
};

/**
 * Given a UTC datetime-local input string (YYYY-MM-DDTHH:mm), return the formatted Local string
 * @param {string} inputValue
 * @returns {string}
 */
export const formatUtcInputToLocalDisplay = (inputValue) => {
  if (!inputValue) return "";
  try {
    const d = new Date(`${inputValue}:00.000Z`);
    if (isNaN(d.getTime())) return "";
    return formatLocalDateTime(d, { showSeconds: false });
  } catch {
    return "";
  }
};

/**
 * Get current UTC datetime formatted for datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns {string}
 */
export const getCurrentUtcInput = () => {
  return new Date().toISOString().slice(0, 16);
};

/**
 * Add days to a UTC datetime-local input string or now
 * @param {string} baseInput - format "YYYY-MM-DDTHH:mm"
 * @param {number} daysToAdd
 * @returns {string}
 */
export const addDaysToUtcInput = (baseInput, daysToAdd = 7) => {
  try {
    const baseDate = baseInput ? new Date(`${baseInput}:00.000Z`) : new Date();
    if (isNaN(baseDate.getTime())) return "";
    const targetDate = new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
    return targetDate.toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

/**
 * Calculate duration between two UTC datetime-local inputs
 * @param {string} startInput
 * @param {string} endInput
 * @returns {string|null}
 */
export const calculateDuration = (startInput, endInput) => {
  if (!startInput || !endInput) return null;
  try {
    const start = new Date(`${startInput}:00.000Z`).getTime();
    const end = new Date(`${endInput}:00.000Z`).getTime();
    if (isNaN(start) || isNaN(end) || end <= start) return null;

    const diffMs = end - start;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffHours / 24);
    const hours = diffHours % 24;

    if (days > 0 && hours > 0) {
      return `${days}d ${hours}h (${diffHours}h total)`;
    } else if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} (${diffHours}h total)`;
    } else {
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
  } catch {
    return null;
  }
};
